import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import type {
  FetchDataSourceResult,
  InterpolatedRequest,
} from './fetchDataSource';

/**
 * Host-supplied endpoints for source types whose URL is not authored.
 *
 * Collapses what was a growing per-kind provider-prop list into one argument.
 * A new source type needing its own host endpoint adds a field here rather
 * than a parameter to every function between the provider and the fetcher.
 */
export interface DataSourceHostConfig {
  readonly reportingQueryUrl?: string;
  readonly inferenceUrl?: string;
}

export interface DispatchOptions {
  readonly signal?: AbortSignal;
  readonly trustedOrigins?: readonly string[];
  readonly fetchImpl?: typeof fetch;
  readonly setTimeoutImpl?: typeof setTimeout;
  readonly clearTimeoutImpl?: typeof clearTimeout;
  readonly delay?: (ms: number, signal?: AbortSignal) => Promise<void>;
  readonly coerceRequestBody?: (
    req: InterpolatedRequest,
  ) => InterpolatedRequest;
  readonly coerceResponse?: (
    res: unknown,
    req: InterpolatedRequest,
    decl?: DataSourceDeclaration,
  ) => unknown;
}

/**
 * Everything the runtime needs to know about one source type, in one place.
 *
 * The problem this replaces: the same per-kind question was answered in six
 * files that had to stay mutually consistent, and two of the six failed
 * silently when missed — a forgotten cache-key branch lets two different
 * requests share a cached result, and a forgotten interpolatable-field branch
 * leaves a chart not reacting to a filter change. Neither raises anything.
 *
 * {@link buildRequest} is what makes the cache key safe. It returns the
 * **whole** request. Dispatch calls it, applies the host coercer, and passes
 * that request to {@link fetch}. The cache key calls the same pure function
 * and the same coercer, so the key describes the request that is sent even
 * though it is not the same object. Previously each fetcher shaped its
 * request and `deriveCacheKeyForDecl` re-shaped it alongside, which is the
 * drift the two silent branches were.
 *
 * A descriptor is the request-direction mirror of a render adapter: an adapter
 * maps response rows to component props, this maps authored fields to a
 * request.
 */
export interface DataSourceTypeDescriptor<
  TDecl extends DataSourceDeclaration = DataSourceDeclaration,
> {
  /** Discriminator value this type is registered under. */
  readonly kind: string;

  /**
   * Full request identity, before the host's `coerceRequestBody` hook.
   *
   * Must be pure and must include every field that distinguishes one request
   * from another. Dispatch calls this once, applies the host coercer, and
   * passes that request to {@link fetch}. The cache key calls this function
   * again with the same coercer. A field the transport sends but this omits
   * is a field two different requests can disagree on while sharing a cache
   * entry.
   */
  buildRequest(
    decl: TDecl,
    context: unknown,
    host: DataSourceHostConfig,
  ): InterpolatedRequest;

  /**
   * Issue the request dispatch already built, and classify the outcome.
   *
   * The request argument is that built request after the host coercer. Sending
   * a different one — by calling `buildRequest` again, or by reading the
   * declaration back into a URL — puts a request on the wire that the cache
   * key does not describe.
   *
   * Deliberately per-type rather than one shared transport. An author-supplied
   * URL and a host-derived URL warrant different scrutiny, and the reporting
   * and inference fetchers carry an explicit instruction not to be DRY'd with
   * the HTTP one. Keeping this on the descriptor means each type's allowlist
   * and origin checks stay in a module someone can audit on its own.
   */
  fetch(
    request: InterpolatedRequest,
    decl: TDecl,
    options: DispatchOptions & DataSourceHostConfig,
  ): Promise<FetchDataSourceResult>;

  /** Whether a declaration carries enough to be registered with the manager. */
  isSupported(decl: TDecl): boolean;

  /**
   * Values that may contain `{{…}}` templates. Drives both re-fetch triggers
   * and, unless {@link dependencyFields} narrows it, deferral.
   */
  interpolatableFields(decl: TDecl): readonly unknown[];

  /**
   * Fields whose unresolved tokens defer the fetch. Defaults to
   * {@link interpolatableFields}. HTTP narrows it to URL-shaping fields
   * because optional POST criteria are safe to send unresolved.
   */
  dependencyFields?(decl: TDecl, context: unknown): readonly unknown[];

  /**
   * Fields that must resolve before the request may run at all, on top of
   * {@link dependencyFields}. Exists for the case where the general rule is
   * wrong for one operation rather than for the whole type.
   */
  requiredResolvedFields?(decl: TDecl): readonly unknown[];

  /**
   * Whether a run that was not explicitly forced must be skipped. This is the
   * cost gate: an inference source set to `trigger: 'explicit'` must not fire
   * on mount or on a `formState` write.
   */
  deferUntilForced?(decl: TDecl): boolean;

  /**
   * Interpolation roots this type computes for one declaration from the
   * shared context. The manager adds them before the readiness check, the
   * cache key, and the fetch, so all three read the same values, and a
   * missing path under one of them does not defer the fetch. A root here
   * wins over a data source of the same name for this declaration only.
   */
  derivedRoots?(
    decl: TDecl,
    context: unknown,
  ): Readonly<Record<string, unknown>> | undefined;
}

/** Members a descriptor cannot omit. Checked at registration, not at first use. */
const REQUIRED_MEMBERS = [
  'buildRequest',
  'fetch',
  'isSupported',
  'interpolatableFields',
] as const satisfies readonly (keyof DataSourceTypeDescriptor)[];

const OPTIONAL_MEMBERS = [
  'dependencyFields',
  'requiredResolvedFields',
  'deferUntilForced',
  'derivedRoots',
] as const satisfies readonly (keyof DataSourceTypeDescriptor)[];

export class DataSourceTypeRegistrationError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'DataSourceTypeRegistrationError';
    Object.setPrototypeOf(this, DataSourceTypeRegistrationError.prototype);
  }
}

export interface DataSourceTypeRegistry {
  register<TDecl extends DataSourceDeclaration>(
    descriptor: DataSourceTypeDescriptor<TDecl>,
  ): void;
  /** The descriptor for `decl`'s kind, or `null` for an unregistered kind. */
  resolve(decl: DataSourceDeclaration): DataSourceTypeDescriptor | null;
  get(kind: string): DataSourceTypeDescriptor | null;
  kinds(): readonly string[];
}

/**
 * Normalized discriminator. A declaration with no `kind` is HTTP — that is the
 * shape every configuration authored before the field existed has, and the
 * publish-time schema in `dynamic-flows` routes it the same way.
 */
export function readDeclaredKind(decl: DataSourceDeclaration): string {
  const kind = (decl as { kind?: unknown }).kind;
  if (kind === undefined || kind === 'http') {
    return 'http';
  }
  return typeof kind === 'string' ? kind : String(kind);
}

export function createDataSourceTypeRegistry(): DataSourceTypeRegistry {
  const byKind = new Map<string, DataSourceTypeDescriptor>();

  return {
    register(descriptor) {
      assertCompleteDescriptor(descriptor);
      if (byKind.has(descriptor.kind)) {
        throw new DataSourceTypeRegistrationError(
          `Data source kind "${descriptor.kind}" is already registered. Two descriptors for one kind would make dispatch depend on registration order.`,
        );
      }
      byKind.set(
        descriptor.kind,
        descriptor as unknown as DataSourceTypeDescriptor,
      );
    },
    resolve(decl) {
      return byKind.get(readDeclaredKind(decl)) ?? null;
    },
    get(kind) {
      return byKind.get(kind) ?? null;
    },
    kinds() {
      return Array.from(byKind.keys());
    },
  };
}

/**
 * Reject an incomplete descriptor at registration.
 *
 * The whole point of 5.3 is removing branches that failed silently when a
 * source type was added and one site was missed. Accepting a descriptor with
 * no `buildRequest` and discovering it at the first fetch would reproduce that
 * failure mode with a registry in front of it.
 */
function assertCompleteDescriptor(descriptor: unknown): void {
  if (descriptor === null || typeof descriptor !== 'object') {
    throw new DataSourceTypeRegistrationError(
      'A data source type descriptor must be an object.',
    );
  }
  const candidate = descriptor as Record<string, unknown>;
  const kind = candidate.kind;
  if (typeof kind !== 'string' || kind.trim().length === 0) {
    throw new DataSourceTypeRegistrationError(
      'A data source type descriptor must declare a non-empty string `kind`.',
    );
  }

  const missing = REQUIRED_MEMBERS.filter(
    (member) => typeof candidate[member] !== 'function',
  );
  if (missing.length > 0) {
    throw new DataSourceTypeRegistrationError(
      `Data source type "${kind}" is missing required member(s): ${missing.join(', ')}.`,
    );
  }

  const malformedOptional = OPTIONAL_MEMBERS.filter(
    (member) =>
      candidate[member] !== undefined &&
      typeof candidate[member] !== 'function',
  );
  if (malformedOptional.length > 0) {
    throw new DataSourceTypeRegistrationError(
      `Data source type "${kind}" declares non-function optional member(s): ${malformedOptional.join(', ')}.`,
    );
  }
}
